/**
 * How each app category looks — a gradient and one oversized glyph — for the
 * category tiles, a category page's banner, and an app icon's fallback. Keyed
 * by the platform's permanent category ids (`APP_CATEGORIES` in
 * @fias/db-types); a category this file does not know yet gets the neutral
 * look. Glyphs are plain text, as the genre looks are.
 */
import type { GenreLook } from '../genres';

const TEXT_FORM = String.fromCodePoint(0xfe0e);

const LOOKS: Record<string, GenreLook> = {
  'ai-assistants': { from: '#8B7CFF', to: '#3B2BD1', glyph: '✦' },
  productivity: { from: '#4FA3FF', to: '#1F56D6', glyph: '✓' },
  business: { from: '#5F7A99', to: '#22324A', glyph: '▤' },
  finance: { from: '#3CD08A', to: '#0B7A55', glyph: '$' },
  communication: { from: '#52C7FF', to: '#1B7BD9', glyph: `✉${TEXT_FORM}` },
  social: { from: '#FF7AA2', to: '#C21F5C', glyph: '◉' },
  'graphics-design': { from: '#FF8A4C', to: '#C4317A', glyph: '◐' },
  'photo-video': { from: '#FFC24C', to: '#D8481C', glyph: '◫' },
  'music-audio': { from: '#FF6A95', to: '#6D32F0', glyph: '♪' },
  education: { from: '#3DD5C5', to: '#2558E0', glyph: '✎' },
  'developer-tools': { from: '#6E7A8C', to: '#1F2430', glyph: '{ }' },
  utilities: { from: '#A6B1BF', to: '#4A5363', glyph: '⚙' },
  entertainment: { from: '#FF5F6D', to: '#8E2DE2', glyph: '★' },
  lifestyle: { from: '#FFB38A', to: '#C2556B', glyph: '❦' },
  'health-fitness': { from: '#FF6A6A', to: '#C21F3B', glyph: '♥' },
  travel: { from: '#5CCBFF', to: '#2468E8', glyph: `✈${TEXT_FORM}` },
  'food-drink': { from: '#FFB341', to: '#C2410C', glyph: '◒' },
  shopping: { from: '#F7D154', to: '#C2750A', glyph: '◇' },
  news: { from: '#9AA4B2', to: '#3D4552', glyph: '¶' },
  reference: { from: '#7FB2FF', to: '#3A4FB8', glyph: '§' },
  sports: { from: '#38D07F', to: '#0F7450', glyph: '◎' },
};

const NEUTRAL: GenreLook = { from: '#A1A1AA', to: '#52525B', glyph: '✦' };

export function categoryLook(categoryId: string): GenreLook {
  return LOOKS[categoryId] ?? NEUTRAL;
}

/** The category's gradient, deep at the bottom left where its label sits. */
export function categoryGradient(categoryId: string): string {
  const { from, to } = categoryLook(categoryId);
  return `linear-gradient(to top right, ${to}, ${from})`;
}

/** Every category id this file has a look for. */
export const KNOWN_CATEGORY_IDS: readonly string[] = Object.keys(LOOKS);
